import { lazy, type ComponentType } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { IS_LOCAL_MODE } from './services/auth';
import { Layout } from './components/layout/Layout';
import { RequireArea, RequireAuth, RequireOwner } from './components/layout/RouteGuards';
import { AccountNotAuthorized, DisabledAccount } from './components/layout/AuthGates';
import { PageSkeleton } from './components/ui/Skeleton';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { TooltipProvider } from './components/ui/Tooltip';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { Landing } from './pages/Landing';

// Pages are code-split per route (they export named components).
function lazyPage<M>(load: () => Promise<M>, pick: (m: M) => ComponentType<any>) {
  return lazy(() => load().then((m) => ({ default: pick(m) })));
}

const Dashboard = lazyPage(() => import('./pages/Dashboard'), (m) => m.Dashboard);
const Tasks = lazyPage(() => import('./pages/Tasks'), (m) => m.Tasks);
const Minutes = lazyPage(() => import('./pages/Minutes'), (m) => m.Minutes);
const Import = lazyPage(() => import('./pages/Import'), (m) => m.Import);
const Export = lazyPage(() => import('./pages/Export'), (m) => m.Export);
const ScreenshotHistory = lazyPage(() => import('./pages/ScreenshotHistory'), (m) => m.ScreenshotHistory);
const ExtensionDownload = lazyPage(() => import('./pages/ExtensionDownload'), (m) => m.ExtensionDownload);
const Profile = lazyPage(() => import('./pages/Profile'), (m) => m.Profile);
const Settings = lazyPage(() => import('./pages/Settings'), (m) => m.Settings);
const Trash = lazyPage(() => import('./pages/Trash'), (m) => m.Trash);
const UsersManagement = lazyPage(() => import('./pages/UsersManagement'), (m) => m.UsersManagement);
const ProjectGantt = lazyPage(() => import('./pages/ProjectGantt'), (m) => m.ProjectGantt);
const KpiManagement = lazyPage(() => import('./pages/KpiManagement'), (m) => m.KpiManagement);
const KpiCoverage = lazyPage(() => import('./pages/KpiCoverage'), (m) => m.KpiCoverage);
const CategoryManagement = lazyPage(() => import('./pages/CategoryManagement'), (m) => m.CategoryManagement);
const RicohSpaces = lazyPage(() => import('./pages/RicohSpaces'), (m) => m.RicohSpaces);
const RicohSpacesDashboard = lazyPage(() => import('./pages/RicohSpacesDashboard'), (m) => m.RicohSpacesDashboard);
const RicohSpacesTasks = lazyPage(() => import('./pages/RicohSpacesTasks'), (m) => m.RicohSpacesTasks);
const UiKit = lazyPage(() => import('./pages/UiKit'), (m) => m.UiKit);
const Home = lazyPage(() => import('./pages/Home'), (m) => m.Home);

/** "/" - the Home summary for signed-in users, the Landing (sign-in) page for everyone else. */
function HomeRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (IS_LOCAL_MODE || isAuthenticated) return <Home />;
  if (loading) return <PageSkeleton />;
  return <Landing />;
}

/** The old Project Dashboard lives on inside the Gantt page; keep bookmarks working. */
function ProjectDashboardRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/project-gantt', search }} replace />;
}

function AppRoutes() {
  const { unauthorized, disabled } = useAuth();

  if (unauthorized) return <AccountNotAuthorized />;
  if (disabled) return <DisabledAccount />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route element={<RequireAuth />}>
          <Route path="/profile" element={<Profile />} />

          <Route element={<RequireArea area="task-management" />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/minutes" element={<Minutes />} />
            <Route path="/minutes/:pageId" element={<Minutes />} />
            <Route path="/import" element={<Import />} />
            <Route path="/export" element={<Export />} />
            <Route path="/screenshot-history" element={<ScreenshotHistory />} />
            <Route path="/extension" element={<ExtensionDownload />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/trash" element={<Trash />} />
          </Route>

          <Route element={<RequireArea area="pj-kpi-management" />}>
            <Route path="/project-gantt" element={<ProjectGantt />} />
            <Route path="/project-dashboard" element={<ProjectDashboardRedirect />} />
            <Route path="/kpi" element={<KpiManagement />} />
            <Route path="/kpi-coverage" element={<KpiCoverage />} />
            <Route path="/categories" element={<CategoryManagement />} />
          </Route>

          <Route element={<RequireArea area="ricoh-spaces" />}>
            <Route path="/ricoh-spaces" element={<RicohSpacesDashboard />} />
            <Route path="/ricoh-spaces/opportunities" element={<RicohSpaces />} />
            <Route path="/ricoh-spaces/tasks" element={<RicohSpacesTasks />} />
          </Route>

          <Route element={<RequireOwner />}>
            <Route path="/users" element={<UsersManagement />} />
          </Route>

          {/* Component gallery for development only (never shipped) */}
          {import.meta.env.DEV && <Route path="/ui-kit" element={<UiKit />} />}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <BrowserRouter>
          <ToastProvider>
            <ConfirmProvider>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </ConfirmProvider>
          </ToastProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  );
}
